// Package imports:
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Component imports:
import Table from 'react-bootstrap/Table';
// Action imports:
import { requestShareNews } from '../../../redux-store/actions/newsActions';
// Service imports:
import { formatDate, turnDateToSimpleTime } from '../../../services/utils';
// Type imports:
import { RootState } from '../../../redux-store/reducers';
import { AlertTickData } from '../../../types/api/AlertTypes';

interface IOwnProps {
    alert: AlertTickData,
}
type ReduxProps = ConnectedProps<typeof connector>;
type Props = IOwnProps & ReduxProps;

const AlertInfoV2: React.FC<Props> = ({
    alert,
    newsDoubleMap,
    accessTokenV2,
    requestShareNews
}) => {
    // Fetch news data.
    useEffect(() => {
        const { symbol, newstimestamp } = alert;
        if (symbol === null || newstimestamp === null) return;

        const newsDate = turnDateToSimpleTime(new Date(newstimestamp));

        if (newsDoubleMap?.[symbol]?.[newsDate] === undefined) {
            requestShareNews(symbol, newsDate, accessTokenV2);
        }
    }, [newsDoubleMap, alert, accessTokenV2, requestShareNews]);

    const displayConditionalRow = (title: string, value: any, isDate?: boolean) => {
        if (value === undefined || value === null) return null;
        return (
            <tr>
                <th>{title}</th>
                <td>
                    {
                        (typeof value === 'boolean') // if
                        // To display boolean values, cast them to string.
                        ? `${value}`
                        : (isDate) // else if
                        ? formatDate(value) 
                        : value // else
                    }
                </td>
            </tr>
        );
    }

    const getNewsLink = () => {
        const { symbol, newstimestamp, newstitle } = alert;
        if (
            symbol === null
            || newstimestamp === null
            || newstitle === null
        ) return newstitle;

        const newsDate = turnDateToSimpleTime(new Date(newstimestamp));
        const newsArray = newsDoubleMap?.[symbol]?.[newsDate];

        if (
            newsArray === undefined
            || newsArray === 'waiting'
            || newsArray instanceof Error
        ) return newstitle;

        interface NewsItem {
            title: string;
            link: string;
            [key: string]: any;
        }

        const matchingNewsItem: NewsItem | undefined = (newsArray as NewsItem[]).find(
            (newsItem: NewsItem) => newsItem.title === newstitle
        );

        if (matchingNewsItem === undefined) return newstitle;

        return (
            <a target='_blank' rel='noopener noreferrer' href={matchingNewsItem.link}>
                {matchingNewsItem.title}
            </a>
        );
    }

    return (
        <div className='AlertInfo'>
            <Table className='SimpleCompressedTable'>
                <tbody>
                    {displayConditionalRow('Alert', alert.description)}
                    {displayConditionalRow('Description', alert.description_long)}
                    {displayConditionalRow('Alert Created', alert.ad_created_timestamp, true)}
                    {displayConditionalRow('Reference Timestamp', alert.ref_timestamp, true)}
                    {displayConditionalRow('Details', alert.ad_description)}
                    {displayConditionalRow('Symbol', alert.symbol)}
                    {displayConditionalRow('Order ID', alert.order_id)}
                    {displayConditionalRow('Trade ID', alert.trade_id)}
                    {displayConditionalRow('Value of Order', alert.order_value)}
                    {displayConditionalRow('Outside Spread', alert.outside_spread)}
                    {displayConditionalRow('News Title', getNewsLink())}
                    {displayConditionalRow('News Time', alert.newstimestamp, true)}
                    {displayConditionalRow('Generated order limit', alert.generated_limit)}
                </tbody>
            </Table>
        </div>
    )
}

function mapStateToProps({ newsReducer, loginReducer }: RootState) {
    const { newsDoubleMap } = newsReducer;
    const { accessTokenV2 } = loginReducer
    return {
        newsDoubleMap,
        accessTokenV2
    }
}

const connector = connect(mapStateToProps, { requestShareNews });
export default connector(AlertInfoV2);